/*
 * The single door to the API. Everything the client sends to the server goes
 * through here, so three concerns live in exactly one place:
 *
 *   1. Bearer auth. The access token is attached to every authenticated call.
 *   2. Silent rotation. A 401 triggers one refresh attempt (single-flight, so
 *      ten concurrent 401s cause one refresh, not ten) and the call retries.
 *   3. RFC 9457. The server speaks problem+json; a failure becomes an ApiError
 *      carrying the machine-readable `code`, so screens branch on code, never
 *      on a parsed English string.
 */

export interface Session {
  userId: string;
  accountId: string;
  roles: string[];
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = 'pms.session';

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function saveSession(s: Session | null): void {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

/** A typed failure. `code` is the stable server code (e.g. "forbidden"). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when refresh fails: the caller should return the user to sign-in. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, 'session_expired', 'Your session has ended. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

let onExpired: (() => void) | null = null;
/** The auth layer registers here so an unrecoverable 401 clears app state. */
export function setExpiredHandler(fn: () => void): void {
  onExpired = fn;
}

let onMfaRequired: (() => void) | null = null;
/**
 * The auth layer registers here so a 403 `mfa_enrolment_required` — the
 * server's way of saying "this platform session has not set up its second
 * factor" — sends the person to the enrolment screen from wherever they were,
 * rather than each admin page having to recognise the code itself.
 */
export function setMfaRequiredHandler(fn: () => void): void {
  onMfaRequired = fn;
}

let refreshInFlight: Promise<Session> | null = null;

async function doRefresh(current: Session): Promise<Session> {
  const res = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) throw new SessionExpiredError();
  const data = (await res.json()) as Session;
  const next: Session = { ...current, ...data };
  saveSession(next);
  return next;
}

function refresh(current: Session): Promise<Session> {
  // Single-flight: concurrent 401s share one refresh round-trip.
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(current).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Public endpoints (login, register, plans) send no Authorization header. */
  auth?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

async function parse(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function toError(status: number, payload: unknown): ApiError {
  // RFC 9457 problem detail: { type, title, status, detail, code?, ...extra }
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    const code = String(p.title ?? p.code ?? 'error');
    const detail = String(p.detail ?? p.title ?? 'Something went wrong.');
    const { type: _t, title: _ti, status: _s, detail: _d, ...extra } = p;
    return new ApiError(status, code, detail, extra);
  }
  return new ApiError(status, 'error', 'Something went wrong.');
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, signal } = opts;
  const url = buildUrl(path, query);

  const send = async (session: Session | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (auth && session) headers['authorization'] = `Bearer ${session.accessToken}`;
    return fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  let session = loadSession();
  let res = await send(session);

  if (res.status === 401 && auth && session) {
    try {
      session = await refresh(session);
      res = await send(session);
    } catch {
      saveSession(null);
      onExpired?.();
      throw new SessionExpiredError();
    }
  }

  const payload = await parse(res);
  if (!res.ok) {
    const err = toError(res.status, payload);
    if (err.status === 403 && err.code === 'mfa_enrolment_required') onMfaRequired?.();
    throw err;
  }
  return payload as T;
}

/**
 * Fetches an authenticated file and returns an object URL for it.
 *
 * For images on admin screens. An `<img src="/api/v1/admin/...">` sends no
 * Authorization header — the browser simply requests the URL — so every such
 * thumbnail answered 401 and rendered as a broken image. Nothing reported it,
 * because a failed image is silent.
 *
 * The caller owns the returned URL and must revoke it; useAuthedImage does
 * that on unmount.
 */
export async function apiBlobUrl(path: string, query?: RequestOptions['query']): Promise<string> {
  const url = buildUrl(path, query);
  let session = loadSession();

  const send = async (s: Session | null) =>
    fetch(url, { headers: s ? { authorization: `Bearer ${s.accessToken}` } : {} });

  let res = await send(session);
  if (res.status === 401 && session) {
    try {
      session = await refresh(session);
      res = await send(session);
    } catch {
      saveSession(null);
      onExpired?.();
      throw new SessionExpiredError();
    }
  }
  if (!res.ok) throw toError(res.status, await parse(res));
  return URL.createObjectURL(await res.blob());
}

/**
 * The file name the server chose, from Content-Disposition. The server names
 * every tenant document "Unit - Tenant - Number" (documentFileName), so a
 * caller that passes null to apiDownload, or a Preview that has to fall back to
 * saving, ends up with that name rather than a guess built from the URL.
 */
function serverFileName(res: Response): string | null {
  const header = res.headers.get('content-disposition');
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim()); } catch { /* fall through */ }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * Downloads an authenticated file (report CSV). A plain link cannot carry the
 * Authorization header, so the bytes are fetched, wrapped in an object URL and
 * handed to a synthetic anchor. The object URL is revoked immediately after.
 *
 * Pass null for the file name to take the one the server sent.
 */
export async function apiDownload(
  path: string,
  filename: string | null,
  query?: RequestOptions['query'],
): Promise<void> {
  const url = buildUrl(path, query);
  let session = loadSession();

  const send = async (s: Session | null) =>
    fetch(url, { headers: s ? { authorization: `Bearer ${s.accessToken}` } : {} });

  let res = await send(session);
  if (res.status === 401 && session) {
    try {
      session = await refresh(session);
      res = await send(session);
    } catch {
      saveSession(null);
      onExpired?.();
      throw new SessionExpiredError();
    }
  }
  if (!res.ok) throw toError(res.status, await parse(res));

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename ?? serverFileName(res) ?? 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

/**
 * Opens an authenticated file in a new tab instead of saving it, used for
 * Preview and Print, where the reader wants the document on screen with the
 * browser's own print controls rather than a file in their downloads folder.
 *
 * The fetch/blob dance is the same as apiDownload and for the same reason: a
 * plain window.open cannot carry the Authorization header.
 *
 * The tab is opened FIRST, while the click is still fresh, and pointed at the
 * file once the bytes arrive. Two things depend on that order:
 *  - Opening after the fetch is outside the click, and Safari and some Chrome
 *    settings block it as a pop-up.
 *  - It must not pass 'noopener'. That flag makes window.open return null every
 *    time, so a "did it open?" check always says no, and the save-to-disk
 *    fallback below fired on every Preview, named after the last URL segment
 *    ("pdf"). The opener is cut afterwards instead.
 */
export async function apiOpen(path: string, query?: RequestOptions['query']): Promise<void> {
  const url = buildUrl(path, query);
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;

  try {
    let session = loadSession();

    const send = async (s: Session | null) =>
      fetch(url, { headers: s ? { authorization: `Bearer ${s.accessToken}` } : {} });

    let res = await send(session);
    if (res.status === 401 && session) {
      try {
        session = await refresh(session);
        res = await send(session);
      } catch {
        saveSession(null);
        onExpired?.();
        throw new SessionExpiredError();
      }
    }
    if (!res.ok) throw toError(res.status, await parse(res));

    const href = URL.createObjectURL(await res.blob());
    if (tab) {
      tab.location.replace(href);
    } else {
      // Pop-ups blocked: the only case where saving is the right fallback, so
      // the action still does something rather than failing silently. The file
      // keeps the server's name.
      const a = document.createElement('a');
      a.href = href;
      a.download = serverFileName(res) ?? 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoked late: the new tab needs the URL to still resolve when it loads.
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  } catch (e) {
    // Do not leave a blank tab behind a failed Preview.
    tab?.close();
    throw e;
  }
}

/**
 * Whether a stored file is a type a browser tab can actually show, rather than
 * silently failing to render or triggering a bare download that looks like a
 * broken Preview button. A Word document or a spreadsheet is not previewable
 * this way; a PDF or a photograph is. Used to decide when to offer Preview
 * (apiOpen) beside Download (apiDownload) on a list of uploaded documents,
 * whose mime type is not known ahead of time the way a receipt's or an
 * invoice's is.
 */
export function isPreviewableMime(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  return mimeType === 'application/pdf' || mimeType.startsWith('image/');
}

/* ---- auth endpoints (public: no Bearer) --------------------------------- */

/**
 * What login returns INSTEAD of a session when a second factor is due: a
 * five-minute token that proves the password was right and is good for
 * nothing but the second step.
 */
export interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
  /** Which ways through the second step this person has switched on (162). */
  factors?: { authenticator: boolean; email: boolean; sms: boolean };
}

export const isMfaChallenge = (r: Session | MfaChallenge): r is MfaChallenge =>
  (r as MfaChallenge).mfaRequired === true;

export async function apiLogin(identifier: string, password: string): Promise<Session | MfaChallenge> {
  return apiRequest<Session | MfaChallenge>('/api/v1/auth/login', {
    method: 'POST',
    auth: false,
    body: { email: identifier, password },
  });
}

/** The second step: the challenge token plus the six digits. */
export async function apiMfaVerify(mfaToken: string, code: string): Promise<Session> {
  return apiRequest<Session>('/api/v1/auth/mfa/verify', {
    method: 'POST',
    auth: false,
    body: { mfaToken, code },
  });
}

/** Ask for a second-factor code by text message or email instead of the authenticator. */
export async function apiMfaSendCode(way: 'sms' | 'email', mfaToken: string): Promise<{ sent: true; to: string }> {
  return apiRequest<{ sent: true; to: string }>(`/api/v1/auth/mfa/${way}`, {
    method: 'POST',
    auth: false,
    body: { mfaToken },
  });
}

/**
 * Switch text-message codes on or off for the signed-in person. Switching on is
 * two calls: the first texts a code to the number on the profile and answers
 * { pending, to }, the second carries the code back and arms the factor.
 */
export async function apiSmsFactor(input: { enabled: boolean; code?: string }):
  Promise<{ smsFactor: boolean; twoStep: boolean; pending?: boolean; to?: string }> {
  return apiRequest('/api/v1/me/mfa/sms', { method: 'POST', body: input });
}

export interface MfaSetup { secret: string; otpauthUrl: string }

/** Begins enrolment for the signed-in user: a fresh secret to scan. */
export async function apiMfaSetup(): Promise<MfaSetup> {
  return apiRequest<MfaSetup>('/api/v1/auth/mfa/setup', { method: 'POST', body: {} });
}

/** Confirms enrolment with one code and returns a session that carries the
 *  second-factor proof, replacing the one that could not reach the Control
 *  Centre. */
export async function apiMfaConfirm(code: string): Promise<Session> {
  return apiRequest<Session>('/api/v1/auth/mfa/confirm', { method: 'POST', body: { code } });
}

export interface RegisterInput {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  countryCode?: string;
  currencyCode?: string;
  accountType?: string;
  acceptedTerms: boolean;
}

export async function apiRegister(input: RegisterInput): Promise<Session> {
  return apiRequest<Session>('/api/v1/auth/register', {
    method: 'POST',
    auth: false,
    body: input,
  });
}

export async function apiLogout(refreshToken: string): Promise<void> {
  try {
    await apiRequest('/api/v1/auth/logout', { method: 'POST', auth: false, body: { refreshToken } });
  } catch {
    /* logout is best-effort; local state is cleared regardless */
  }
}
